import { Button, Form, Input, Space } from "antd";
import { useEffect } from "react";
import { useAppSelector } from "../../../../app/store";
import BDFlag from "../../../../assets/icons/BDFlag";
import EmailIcon from "../../../../assets/icons/EmailIcon";
import ProfileImg from "../../../../assets/images/avatar.svg";
import UploadProfileImage from "../../../../components/Employees/edit/UploadProfileImage";
import CustomModal from "../../../../components/ui/CustomModal";
import Loader from "../../../../components/ui/Loader";
import { useProfileUpdateMutation } from "../../../../features/auth/authApi";
import {
  useGetEmployeeDetailsQuery,
  useUpdateProfilePictureMutation,
  useUploadPhotoInEmployeeMutation,
} from "../../../../features/employee/employeeApi";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { ModalComponentProps } from "../../../../types";
import { formatPhoneNumber } from "../../../../utils/utils";

export default function EditPersonalProfileModal({
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { data, isLoading: getLoading } = useGetEmployeeDetailsQuery(
    authInfo?.id?.toString() || "",
    {
      skip: !authInfo?.id,
    }
  );
  const [
    uploadPhotoInEmployeeData,
    {
      isLoading: isImgLoading,
      isError: isImgError,
      error: imgError,
      data: imgData,
    },
  ] = useUploadPhotoInEmployeeMutation();
  const [
    updateProfilePicture,
    {
      isLoading: isProfilePicLoading,
      isError: isProfilePicError,
      error: profilePicError,
      isSuccess: isProfilePicSuccess,
    },
  ] = useUpdateProfilePictureMutation();
  const [profileUpdate, { isLoading, isError, error, isSuccess }] =
    useProfileUpdateMutation();

  useError(isImgError, imgError, "Profile image upload fail");
  useError(isProfilePicError, profilePicError, "Profile image upload fail");
  useError(isError, error, "Profile update fail");

  const profile = data?.data?.employee;

  const onFinish = (values: any) => {
    const { mobile, ...rest } = values;
    const newData = {
      ...rest,
      business_member_id: profile?.id,
      profile_id: profile?.member?.profile?.id,
      mobile: mobile ? formatPhoneNumber(mobile) : undefined,
    };

    profileUpdate(newData);
  };

  useEffect(() => {
    if (Object.keys(profile || {})) {
      form.setFieldsValue({
        name: profile?.member?.profile?.name,
        mobile: profile?.mobile,
        email: profile?.member?.profile?.email,
      });
    }
  }, [form, profile]);

  useEffect(() => {
    if (imgData?.Location && authInfo?.id) {
      updateProfilePicture({
        id: authInfo?.id,
        body: {
          pro_pic: imgData?.Location,
        },
      });
    }
  }, [authInfo?.id, imgData?.Location, updateProfilePicture]);

  useEffect(() => {
    if (isProfilePicSuccess) {
      toast("success", "Profile picture updated successfully");
    }
  }, [isProfilePicSuccess]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Profile updated successfully");
      handleClose();
    }
  }, [handleClose, isSuccess]);

  return (
    <CustomModal
      title="Edit Profile"
      handleClose={handleClose}
      {...rest}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {getLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item className="mb-4">
              <UploadProfileImage
                img={profile?.member?.profile?.pro_pic || ProfileImg}
                handleImage={(file) => uploadPhotoInEmployeeData(file)}
                isLoading={isImgLoading || isProfilePicLoading}
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Your Name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter your name",
                },
              ]}
            >
              <Input placeholder="Enter your name" />
            </Form.Item>
            <Form.Item
              name="mobile"
              label="Mobile Number"
              className="mb-4"
              initialValue="+880"
              rules={[
                {
                  validator: async (_: any, value: string) => {
                    if (!value) {
                      return Promise.reject(
                        new Error("Please enter phone number")
                      );
                    }
                    // if (!value) {
                    //   return Promise.resolve();
                    // }

                    const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                    if (!phoneRegex.test(value)) {
                      return Promise.reject(
                        new Error(
                          "Please enter a valid Bangladeshi phone number"
                        )
                      );
                    }
                  },
                },
              ]}
            >
              <Input prefix={<BDFlag />} size="large" placeholder="+880" />
            </Form.Item>
            <Form.Item name="email" label="Email Address" className="mb-4">
              <Input
                placeholder="example@email.com"
                prefix={<EmailIcon />}
                disabled
              />
            </Form.Item>

            <Space className="w-full mt-2" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isLoading}
              >
                Update
              </Button>
            </Space>
          </>
        )}
      </Form>
    </CustomModal>
  );
}
